import React, { useMemo } from 'react';
import {
  FileCardContainer,
  FileCardContent,
  FileCardDownloadButton,
  FileCardFooter,
  FileCardTitle,
  FilePreviewFallback,
  FilePreviewFallbackText,
  FilePreviewImage,
  PdfPreviewFrame,
} from './styled';
import { parseStrapiImage } from 'utils';
import { buildS3DownloadUrl } from 'services/s3Download';
import { Icon } from 'ui/styled';
import { trackEvent } from 'services/analytics';

const withFileExtension = (name, extension) => {
  const cleanName = `${name || 'arquivo'}`.trim() || 'arquivo';
  const cleanExtension = `${extension || ''}`.replace(/^\./, '').trim();
  if (!cleanExtension || cleanName.toLowerCase().endsWith(`.${cleanExtension.toLowerCase()}`)) {
    return cleanName;
  }

  return `${cleanName}.${cleanExtension}`;
};

export default function FileCard({ file }) {

  const currentFile = useMemo(() => {
    const rawFile = file?.file || file?.document || file?.url;
    const fileUrl = typeof rawFile === 'string'
      ? rawFile
      : rawFile?.url
        ? parseStrapiImage(rawFile.url)
        : null;
    const previewImage = file?.image?.url ? parseStrapiImage(file.image.url) : file?.cover;
    const cleanUrl = `${fileUrl || ''}`.split('?')[0].split('#')[0];
    const slices = cleanUrl?.split(".");
    const extension = `${slices?.[slices?.length - 1] || ''}`.toLowerCase();
    const mime = `${file?.file?.mime || file?.document?.mime || file?.mime || ''}`.toLowerCase();

    return {
      url: fileUrl,
      extension,
      previewImage,
      isImage: /^image\//.test(mime) || /^(png|jpe?g|gif|webp|bmp|svg)$/i.test(extension),
      isPdf: mime === 'application/pdf' || extension === 'pdf',
    };
  }, [file]);

  const displayName = file?.title || file?.file?.name || file?.document?.name || 'arquivo';
  const downloadName = withFileExtension(displayName, currentFile?.extension);

  const buildFileUrl = (disposition = 'attachment') => {
    if (!currentFile.url) return;

    return buildS3DownloadUrl({
      sourceUrl: currentFile.url,
      filename: downloadName,
      disposition,
    });
  };

  const handleDownload = () => {
    const url = buildFileUrl('attachment');
    if (!url) return;

    trackEvent('FILE_DOWNLOAD', {
      content_type: 'file',
      content_id: file?.id,
      resource_id: file?.file?.id || file?.document?.id || file?.id,
      content_name: displayName,
      file_extension: currentFile?.extension,
    });
    window.location.href = url;
  };

  const handleContentClick = () => {
    if (currentFile?.isPdf) return;
    handleDownload();
  };

  return (
    <>
      <FileCardContainer>
        <FileCardContent onClick={handleContentClick}>
          {currentFile?.isPdf ? (
            <PdfPreviewFrame src={buildFileUrl('inline')} title={displayName} loading="lazy" />
          ) : currentFile?.previewImage ? (
            <FilePreviewImage src={currentFile.previewImage} alt={file?.title || file?.file?.name || 'PDF preview'} />
          ) : currentFile?.isImage ? (
            <FilePreviewImage src={currentFile.url} alt={file?.title || file?.file?.name || 'File preview'} />
          ) : (
            <FilePreviewFallback>
              <Icon icon='pdf-big' />
              <FilePreviewFallbackText>Preview unavailable</FilePreviewFallbackText>
            </FilePreviewFallback>
          )}
        </FileCardContent>
        <FileCardFooter>
          <FileCardTitle>{displayName}</FileCardTitle>
          <FileCardDownloadButton type="button" onClick={handleDownload}>
            Download
          </FileCardDownloadButton>
        </FileCardFooter>
      </FileCardContainer>
    </>
  );
}
